import { useEffect, useMemo, useState, type CSSProperties } from "react";

type AppCatalogItem = {
  accent: string;
  appStoreUrl?: string;
  category: string;
  description: string;
  featured?: boolean;
  iconUrl?: string;
  id: string;
  name: string;
  stage?: string;
  statusLabel?: string;
  tagline: string;
};

type AppCatalogPayload = {
  apps?: AppCatalogItem[];
  featured: AppCatalogItem | null;
  generatedAt: string;
  publishedApps?: AppCatalogItem[];
  source: string;
  syncedAt: string;
  totalPublished?: number;
  totalUpcoming?: number;
  upcomingApps?: AppCatalogItem[];
};

type CatalogTab = "published" | "comingSoon";

export function OtherAppsPage({ sourceId }: { sourceId: string }) {
  const [payload, setPayload] = useState<AppCatalogPayload | null>(null);
  const [query, setQuery] = useState("");
  const [catalogTab, setCatalogTab] = useState<CatalogTab>("published");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadCatalog() {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/apps/catalog?source=" + encodeURIComponent(sourceId), { credentials: "include" });
      const body = (await response.json()) as AppCatalogPayload & { error?: string };
      if (!response.ok) throw new Error(body.error || "Catalog request failed: " + response.status);
      setPayload(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Other apps could not be loaded");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCatalog();
  }, [sourceId]);

  const normalizedQuery = query.trim().toLowerCase();
  const publishedSource = payload?.publishedApps ?? payload?.apps ?? [];
  const upcomingSource = payload?.upcomingApps ?? [];
  const activeSource = catalogTab === "published" ? publishedSource : upcomingSource;
  const apps = useMemo(() => {
    if (!normalizedQuery) return activeSource;
    return activeSource.filter((app) =>
      [app.name, app.category, app.tagline, app.description].some((value) => String(value ?? "").toLowerCase().includes(normalizedQuery))
    );
  }, [activeSource, normalizedQuery]);

  const featured = catalogTab === "published" ? payload?.featured ?? null : null;
  const visibleFeatured = featured && !normalizedQuery ? featured : null;
  const gridApps = visibleFeatured ? apps.filter((app) => app.id !== visibleFeatured.id) : apps;
  const publishedCount = payload?.totalPublished ?? publishedSource.length;
  const upcomingCount = payload?.totalUpcoming ?? upcomingSource.length;
  const emptyLabel = catalogTab === "published" ? "No matching published apps" : "No matching coming soon apps";

  return (
    <section className="hafsa-apps-page">
      <div className="hafsa-apps-hero">
        <div>
          <span>Hafsa Software</span>
          <h1>More apps for Shopify stores</h1>
        </div>
        <div className="hafsa-apps-count">{payload ? publishedCount + " Published Apps" : "Loading apps"}</div>
      </div>

      <div className="hafsa-apps-toolbar">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search apps" aria-label="Search apps" />
        <div className="hafsa-apps-tabs" role="tablist" aria-label="App catalog status">
          <button
            type="button"
            role="tab"
            aria-selected={catalogTab === "published"}
            className={catalogTab === "published" ? "hafsa-apps-tab active" : "hafsa-apps-tab"}
            onClick={() => setCatalogTab("published")}
          >
            Published <span>{publishedCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={catalogTab === "comingSoon"}
            className={catalogTab === "comingSoon" ? "hafsa-apps-tab active" : "hafsa-apps-tab"}
            onClick={() => setCatalogTab("comingSoon")}
          >
            Coming soon <span>{upcomingCount}</span>
          </button>
        </div>
        <button className="hafsa-apps-refresh" type="button" onClick={() => void loadCatalog()} disabled={loading}>
          {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      {error ? <div className="hafsa-apps-error">{error}</div> : null}
      {loading && !payload ? <div className="hafsa-apps-empty">Loading apps...</div> : null}
      {visibleFeatured ? <AppCard app={visibleFeatured} featured /> : null}
      {payload && gridApps.length ? (
        <div className="hafsa-apps-grid">
          {gridApps.map((app) =>
            catalogTab === "published" ? <AppCard key={app.id} app={app} /> : <ComingSoonCard key={app.id} app={app} />
          )}
        </div>
      ) : null}
      {payload && !gridApps.length ? <div className="hafsa-apps-empty">{emptyLabel}</div> : null}
    </section>
  );
}

function AppCard({ app, featured = false }: { app: AppCatalogItem; featured?: boolean }) {
  return (
    <article className={featured ? "hafsa-app-card hafsa-app-card-featured" : "hafsa-app-card"}>
      <div className="hafsa-app-card-head">
        <span className="hafsa-app-logo" style={{ "--hafsa-app-accent": app.accent } as CSSProperties}>
          <span>{initials(app.name)}</span>
          {app.iconUrl ? <img src={app.iconUrl} alt="" loading="lazy" /> : null}
        </span>
        <div>
          <span className="hafsa-app-category">{app.category}</span>
          <h2>{app.name}</h2>
          <small>{app.tagline}</small>
        </div>
      </div>
      <p>{app.description}</p>
      {app.appStoreUrl ? (
        <a href={withUtm(app.appStoreUrl)} target="_blank" rel="noopener noreferrer">
          {featured ? "View featured app" : "View app"}
        </a>
      ) : null}
    </article>
  );
}

function ComingSoonCard({ app }: { app: AppCatalogItem }) {
  return (
    <article className="hafsa-app-card hafsa-app-card-coming-soon">
      <div className="hafsa-app-card-head">
        <span className="hafsa-app-logo" style={{ "--hafsa-app-accent": app.accent } as CSSProperties}>
          <span>{initials(app.name)}</span>
          {app.iconUrl ? <img src={app.iconUrl} alt="" loading="lazy" /> : null}
        </span>
        <div>
          <span className="hafsa-app-category">{app.category}</span>
          <h2>{app.name}</h2>
          <small>{app.tagline}</small>
        </div>
      </div>
      <p>{app.description}</p>
      <span className="hafsa-coming-soon">Coming soon</span>
    </article>
  );
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "HS";
}

function withUtm(url: string) {
  try {
    const next = new URL(url);
    next.searchParams.set("utm_source", "hafsa_cross_app");
    next.searchParams.set("utm_medium", "embedded_app");
    next.searchParams.set("utm_campaign", "other_apps");
    return next.toString();
  } catch {
    return url;
  }
}
